import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Heart, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { servicesPageData } from '../data/mock';

const Services = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-[#f9f9f6] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-4 block">
            {servicesPageData.hero.subtitle}
          </span>
          <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6">
            {servicesPageData.hero.title}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {servicesPageData.hero.description}
          </p>
        </div>
      </section>

      {/* Dementia Therapy Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left Content */}
            <div>
              <span className="text-[#8B9D77] font-medium text-sm tracking-widest uppercase mb-2 block">
                {servicesPageData.dementiaTherapy.title}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-6">
                {servicesPageData.dementiaTherapy.subtitle}
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg mb-8">
                {servicesPageData.dementiaTherapy.description}
              </p>
              
              {/* Offerings List */}
              <ul className="space-y-3 mb-8">
                {servicesPageData.dementiaTherapy.offerings.map((offering, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#8B9D77] flex-shrink-0" />
                    <span className="text-gray-700">{offering}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right - Feature Cards */}
            <div className="space-y-6">
              {servicesPageData.dementiaTherapy.features.map((feature, index) => (
                <Card 
                  key={index} 
                  className="bg-[#f5f5f0] border-0 p-6 rounded-xl"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              ))}
              
              {/* Quote Card */}
              <div className="bg-gradient-to-br from-[#d4dcc4] to-[#c9b896] rounded-xl p-6 text-center">
                <p className="text-gray-800 italic font-serif mb-2">
                  "{servicesPageData.dementiaTherapy.quote.text}"
                </p>
                <p className="text-[#A88B4A] text-sm font-medium">
                  {servicesPageData.dementiaTherapy.quote.reference}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Death Doula Section */}
      <section className="py-20 bg-[#f9f9f6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left - Feature Cards */}
            <div className="space-y-6 order-2 lg:order-1">
              {servicesPageData.deathDoula.features.map((feature, index) => (
                <Card 
                  key={index} 
                  className="bg-white border-0 p-6 rounded-xl shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              ))}
            </div>

            {/* Right Content */}
            <div className="order-1 lg:order-2">
              <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-2 block">
                {servicesPageData.deathDoula.title}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-6">
                {servicesPageData.deathDoula.subtitle}
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg mb-8">
                {servicesPageData.deathDoula.description}
              </p>
              
              {/* Offerings List */}
              <ul className="space-y-3">
                {servicesPageData.deathDoula.offerings.map((offering, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#A88B4A] flex-shrink-0" />
                    <span className="text-gray-700">{offering}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-4 block">
              Our Approach
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-4">
              {servicesPageData.approach.title}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {servicesPageData.approach.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {servicesPageData.approach.items.map((item, index) => (
              <Card 
                key={index} 
                className="bg-[#f5f5f0] border-0 p-8 rounded-2xl text-center"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </Card>
            ))}
          </div>

          {/* Quote */}
          <div className="text-center">
            <p className="text-xl italic text-gray-700 font-serif mb-2">
              "{servicesPageData.approach.quote.text}"
            </p>
            <p className="text-[#A88B4A] font-medium">
              {servicesPageData.approach.quote.reference}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#f9f9f6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-6">
            {servicesPageData.cta.title}
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            {servicesPageData.cta.description}
          </p>
          <Link to="/contact">
            <Button className="bg-[#A88B4A] hover:bg-[#967A3E] text-white px-10 py-6 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg flex items-center gap-2 mx-auto">
              {servicesPageData.cta.buttonText}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
